import { cn } from "@/utils/ui";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import {
  AlertTriangleIcon,
  CheckCircleIcon,
  type LucideIcon,
  XIcon,
} from "lucide-react";
import { Dialog as DialogPrimitive } from "radix-ui";
import type { ComponentProps, PropsWithChildren } from "react";
import React from "react";
import type { SetRequired } from "type-fest";
import { Button } from "./button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "./dialog";

interface StdModalAction {
  label: string;
  intent?: ComponentProps<typeof Button>["intent"];
  flavor?: ComponentProps<typeof Button>["flavor"];
  fullWidth?: boolean;
  className?: string;
  act?: "confirm" | "cancel";
  onClick?: () => void;
}

interface StdModalBaseProps {
  title?: string;
  description?: string;
  icon?: LucideIcon;
  actions?: StdModalAction[];
  headerDirection?: "row" | "column";
  actionsDirection?: "row" | "column";
  size?: "sm" | "md" | "lg" | "xl" | "full";
  role?: "alertdialog" | "dialog";
}

interface StdModalStyleProps {
  className?: string;
  actionsClassName?: string;
  iconClassName?: string;
  centered?: boolean;
}

interface StdModalControlProps {
  visible?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
  afterClose?: () => void;
}

export type StdModalProps = StdModalBaseProps &
  StdModalStyleProps &
  StdModalControlProps;

function StdModalContent({
  title,
  description,
  icon: Icon,
  iconClassName,
  actions = [],
  className,
  actionsClassName,
  headerDirection = "column",
  actionsDirection = "row",
  size = "md",
  centered = true,
  role = "dialog",

  visible = true,
  onConfirm,
  onCancel,
  onClose,
}: StdModalProps) {
  const sizeClasses = {
    sm: "sm:max-w-sm",
    md: "sm:max-w-md",
    lg: "sm:max-w-lg",
    xl: "sm:max-w-xl",
    full: "sm:max-w-screen-lg",
  };

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogContent
        role={role}
        className={cn(
          sizeClasses[size],
          !visible && "invisible",
          centered
            ? "[&_[data-slot=dialog-title]]:text-center [&_[data-slot=dialog-description]]:text-center"
            : "[&_[data-slot=dialog-title]]:text-left [&_[data-slot=dialog-description]]:text-left",
          className,
        )}
      >
        <DialogHeader
          className={cn(headerDirection === "row" ? "flex-row" : "flex-col")}
        >
          {Icon && (
            <div
              className={cn(
                "mx-auto flex size-12 items-center justify-center rounded-full bg-graya-subtle-bg shrink-0",
                headerDirection === "row" ? "mr-2" : "mb-2",
                iconClassName,
              )}
            >
              <Icon className="size-6" />
            </div>
          )}
          <div className="flex flex-col gap-2">
            {title && <DialogTitle className="text-xl">{title}</DialogTitle>}
            {description && (
              <DialogDescription className="text-base">
                {description}
              </DialogDescription>
            )}
          </div>
        </DialogHeader>

        {actions.length > 0 && (
          <DialogFooter
            className={cn(
              "flex mt-3",
              actionsDirection === "column"
                ? "flex-col gap-2"
                : "items-center gap-3",
              actionsClassName,
            )}
          >
            {actions.map((action, index) => (
              <Button
                // biome-ignore lint/suspicious/noArrayIndexKey: This is a static list
                key={index}
                intent={action.intent}
                flavor={action.flavor}
                onClick={() => {
                  if (action.act === "confirm") {
                    onConfirm?.();
                  }
                  if (action.act === "cancel") {
                    onCancel?.();
                  }

                  action.onClick?.();
                  onClose?.();
                }}
                className={cn(
                  action.fullWidth && "w-full",
                  actionsDirection === "column" && "w-full",
                  action.className,
                )}
              >
                {action.label}
              </Button>
            ))}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

export const StdModal = NiceModal.create((props: StdModalProps) => {
  const modal = useModal();
  return (
    <StdModalContent
      {...props}
      visible={modal.visible}
      onConfirm={() => modal.hide().then(modal.remove)}
      onCancel={() => modal.hide().then(modal.remove)}
      onClose={() => modal.hide().then(modal.remove)}
    />
  );
});

export const StdModalPresets = {
  success: (
    props: SetRequired<StdModalProps, "description">,
  ): StdModalProps => ({
    icon: CheckCircleIcon,
    iconClassName: "bg-accent-ui text-accent-solid",
    actions: [
      {
        label: "Noted",
        intent: "solid",
        flavor: "accent",
        act: "confirm",
        fullWidth: true,
      },
    ],
    ...props,
  }),

  alert: (props: SetRequired<StdModalProps, "description">): StdModalProps => ({
    icon: AlertTriangleIcon,
    iconClassName: "bg-destructivea-ui text-destructive-solid",
    role: "alertdialog",
    actions: [
      {
        label: "Dismiss",
        intent: "solid",
        flavor: "destructive",
        act: "confirm",
      },
    ],
    ...props,
  }),

  confirm: (
    props: SetRequired<StdModalProps, "description">,
  ): StdModalProps => ({
    icon: AlertTriangleIcon,
    iconClassName: "bg-destructivea-ui text-destructive-solid",
    headerDirection: "row",
    size: "lg",
    actions: [
      {
        label: "Cancel",
        intent: "outline",
        flavor: "gray",
        act: "cancel",
      },
      {
        label: "Confirm",
        intent: "solid",
        flavor: "accent",
        act: "confirm",
      },
    ],
    ...props,
  }),
};

export const PlainModal = ({ children }: PropsWithChildren) => {
  const modal = useModal();
  return (
    <Dialog open={modal.visible} onOpenChange={() => modal.hide()}>
      <DialogPortal data-slot="dialog-portal">
        <DialogOverlay />
        <DialogPrimitive.Content
          data-slot="dialog-content"
          className={cn(
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "fixed top-[50%] left-[50%] z-50 w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] shadow-lg sm:max-w-lg",
          )}
          tabIndex={"" as unknown as number} // BUG: somehow the default "-1" is not working
        >
          <DialogPrimitive.Title className="sr-only">
            General Modal
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            This is a modal dialog, you may read the content in it. Sorry but we
            are not able to provide a better screen reader experience at the
            moment.
          </DialogPrimitive.Description>

          {children}
          <DialogPrimitive.Close className="data-[state=open]:text-gray-fg-secondary absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4">
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};

export interface PlainModalComponentProps {
  closeModal: () => void;
}

export const usePlainModal = <
  C extends React.FC,
  P extends Partial<ComponentProps<C>>,
>(
  children: C,
  props?: P,
) => {
  const modalInstance = NiceModal.create((compProps: P) => {
    // biome-ignore lint/correctness/useHookAtTopLevel: <explanation>
    const modal = useModal();
    return (
      <PlainModal>
        {React.createElement(children, {
          ...compProps,
          closeModal: () => {
            modal.hide().then(modal.remove);
          },
        })}
      </PlainModal>
    );
  });

  return useModal(modalInstance, { ...props });
};
